// Recuperação e Prontidão — ESTIMATIVAS derivadas do que já está no banco.
//
// ⛔ NENHUM TIPO NOVO DE PERMISSÃO. Tudo aqui sai de `health_metrics`
// (metric_type 'hrv' e 'heart_rate') e de `sleep_logs`, que o app JÁ lê com as
// permissões de hoje — HEALTH_READ_TYPES continua sendo sleep, heartRate,
// heartRateVariability, steps, workouts. Em particular:
//
//   🔴 FREQUÊNCIA CARDÍACA EM REPOUSO É DERIVADA, não pedida. É o MÍNIMO da
//   janela noturna de `heart_rate`. Pedir `restingHeartRate` como tipo novo
//   reabriria a folha de permissão — e há uma versionCode em análise. A
//   derivada é boa o bastante: o mínimo da noite é o proxy que a literatura de
//   wearable de consumo usa, e é o que o próprio relógio faz por baixo.
//
// ═════════════════════════════════════════════════════════════════════════
// 🔴 O RÓTULO É PARTE DA FUNÇÃO, NÃO DA TELA
// ═════════════════════════════════════════════════════════════════════════
// `EstimatedScore.estimate` é `true` no TIPO — não é um booleano que a tela
// pode esquecer de ler, é uma constante que o compilador obriga a existir. O
// número que sai daqui NUNCA se apresenta como medição.
//
// Por quê: rastreador de consumo acerta 60–80% no estagiamento do sono
// (J Clin Sleep Med, 2026). É a honestidade do rótulo que permite entregar
// isto hoje em vez de esperar um sensor que já está no bolso da pessoa.
//
// ═════════════════════════════════════════════════════════════════════════
// ⚠️ AS ÂNCORAS SÃO ESCOLHA DE PRODUTO, E ESTÃO TODAS NUM LUGAR SÓ
// ═════════════════════════════════════════════════════════════════════════
// Não existe constante clínica para "70 = sua linha de base". O que existe é o
// método: comparar a pessoa COM ELA MESMA, nunca com uma população. Toda a
// escala vive em ANCHORS abaixo, para ajustar num lugar só — e para que quem
// discordar dos números discuta os números, não cace mágica no meio da conta.
//
// PURO de propósito: sem React e sem Supabase.
// `now` entra por parâmetro (mesma escolha de metricAge e do engine de
// analytics), então tudo aqui é testável em vitest sem browser e sem relógio.

import { sleepDurationValue, sleepScoreValue } from "../sleep/values";

/** Uma leitura de `health_metrics`: instante em ms + valor. */
export interface MetricPoint {
  /** epoch ms */
  t: number;
  v: number;
}

/** Uma noite de `sleep_logs`, no formato que a tabela guarda. */
export interface NightInput {
  /** coluna DATE `sleep_date`, "YYYY-MM-DD". */
  date: string;
  /** hora do dia, "HH:MM" ou "HH:MM:SS" — NÃO é timestamp. */
  bedtime: string;
  wakeup: string;
  durationHours: number | null;
  /** score 0..100 que o app já calcula; null quando a noite não tem. */
  score: number | null;
}

export type MissingInput = "hrv" | "resting_hr" | "sleep" | "baseline";

export interface ScorePart {
  key: "hrv" | "resting_hr" | "sleep" | "hrv_trend";
  /** 0..100 */
  score: number;
  /** peso efetivo depois de redistribuir o que faltou (soma 1). */
  weight: number;
}

export interface EstimatedScore {
  /** 0..100 quando dá para calcular; null quando não dá. NUNCA um chute. */
  value: number | null;
  /**
   * SEMPRE `true`, e é literal no tipo: este número é derivado, não medido.
   * A tela é obrigada a poder dizer isso porque o dado carrega a marca.
   */
  estimate: true;
  /** O que faltou. Vazio quando `value` existe. */
  missing: MissingInput[];
  /**
   * Quantas noites ainda faltam para a linha de base fechar. `0` quando já
   * fechou. É o que permite a tela dizer "faltam 2 noites" em vez de "—".
   */
  nightsNeeded: number;
  /** As partes que formaram o número, para a tela explicar em vez de decretar. */
  parts: ScorePart[];
}

// ── Âncoras ──────────────────────────────────────────────────────────────────

export const ANCHORS = {
  /** Noites de histórico exigidas antes de qualquer número sair. */
  MIN_BASELINE_NIGHTS: 3,
  /** Teto da janela de linha de base (noites anteriores consideradas). */
  BASELINE_NIGHTS: 14,
  /** Amostras de FC mínimas dentro da noite para o mínimo valer como repouso. */
  MIN_HR_SAMPLES: 3,
  /** Pontuação de quem está EXATAMENTE na própria linha de base. */
  AT_BASELINE: 70,
  /** Quanto sobe/desce do AT_BASELINE nos extremos da banda. */
  SPAN: 30,
  /** Banda relativa de HRV: ±30% em torno da base cobre o intervalo inteiro. */
  HRV_BAND: 0.30,
  /** FC em repouso varia MENOS em termos relativos — banda mais estreita. */
  RHR_BAND: 0.10,
  /** Tendência de HRV: média curta contra a base, banda intermediária. */
  HRV_TREND_BAND: 0.20,
  /** Quantas noites formam a "média curta" da tendência. */
  TREND_NIGHTS: 3,
  /** Piso da escala. Zero seria dizer "você não existe hoje". */
  FLOOR: 25,
  /** Pesos de Recuperação (redistribuídos quando a FC em repouso falta). */
  RECOVERY_WEIGHTS: { hrv: 0.50, resting_hr: 0.25, sleep: 0.25 },
  /** Pesos de Prontidão. */
  READINESS_WEIGHTS: { sleep: 0.60, hrv_trend: 0.40 },
} as const;

// ── Utilidades puras ─────────────────────────────────────────────────────────

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Mediana. Base robusta: uma noite terrível não move a régua da pessoa. */
export function median(values: number[]): number | null {
  const xs = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = xs.length >> 1;
  return xs.length % 2 === 1 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

/**
 * Razão contra a linha de base → 0..100.
 *
 * `higherIsBetter=false` inverte (é o caso da FC em repouso: menor é melhor).
 * Na base exata devolve AT_BASELINE; nos extremos da banda, AT_BASELINE ± SPAN.
 */
export function scoreFromRatio(ratio: number, band: number, higherIsBetter: boolean): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return ANCHORS.AT_BASELINE;
  const delta = (ratio - 1) / band;
  const signed = higherIsBetter ? delta : -delta;
  return Math.round(clamp(ANCHORS.AT_BASELINE + ANCHORS.SPAN * signed, ANCHORS.FLOOR, 100));
}

/**
 * A janela da noite, em epoch ms, reconstruída de `sleep_date` + horas.
 *
 * ⚠️ `bedtime`/`wakeup` são HORA DO DIA na tabela, não timestamp — e a noite
 * atravessa a meia-noite. A convenção segue a que `calculateDuration` já usa
 * em useSleep: se o despertar é numericamente ANTES do deitar, ele é do dia
 * seguinte. `sleep_date` é o dia do DESPERTAR (é assim que o app grava), então
 * a âncora é o despertar e o deitar recua.
 */
export function nightWindow(night: NightInput): { start: number; end: number } | null {
  const day = Date.parse(night.date + "T00:00:00");
  if (!Number.isFinite(day)) return null;

  const toMin = (hhmm: string): number | null => {
    const parts = hhmm.split(":");
    const h = Number(parts[0]);
    const m = Number(parts[1] ?? 0);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };

  const bed = toMin(night.bedtime);
  const wake = toMin(night.wakeup);
  if (bed === null || wake === null) return null;

  const end = day + wake * 60_000;
  // Deitar depois do despertar no relógio ⇒ foi na véspera.
  const start = bed > wake ? day - (24 * 60 - bed) * 60_000 : day + bed * 60_000;
  return { start, end };
}

function within(points: MetricPoint[], w: { start: number; end: number }): number[] {
  const out: number[] = [];
  for (const p of points) {
    if (!Number.isFinite(p.v)) continue;
    if (p.t >= w.start && p.t <= w.end) out.push(p.v);
  }
  return out;
}

/**
 * 🔴 FC EM REPOUSO, DERIVADA: o MÍNIMO da janela noturna.
 *
 * Exige MIN_HR_SAMPLES leituras dentro da noite. Com uma leitura só, "mínimo"
 * é a própria leitura — pode ser um pico de despertar, e um número errado num
 * módulo de saúde é pior que número nenhum.
 */
export function restingHeartRate(heartRate: MetricPoint[], night: NightInput): number | null {
  const w = nightWindow(night);
  if (!w) return null;
  const vals = within(heartRate, w);
  if (vals.length < ANCHORS.MIN_HR_SAMPLES) return null;
  return Math.min(...vals);
}

/** HRV da noite: a MEDIANA das leituras dentro da janela. */
export function nightlyHrv(hrv: MetricPoint[], night: NightInput): number | null {
  const w = nightWindow(night);
  if (!w) return null;
  const vals = within(hrv, w);
  if (vals.length === 0) return null;
  return median(vals);
}

/** Sub-score de sono: o score que o app já calcula; sem ele, duração ÷ alvo. */
export function sleepSubScore(night: NightInput, targetHours: number): number | null {
  const score = sleepScoreValue(night.score);
  if (score !== null) return Math.round(score);
  const duration = sleepDurationValue(night.durationHours), target = sleepDurationValue(targetHours);
  return duration !== null && target !== null && target > 0
    ? Math.round(clamp((duration / target) * 100, 0, 100))
    : null;
}

// ── Entrada dos dois scores ──────────────────────────────────────────────────

export interface ScoreInput {
  /** Noites em qualquer ordem; a função ordena e escolhe a mais recente. */
  nights: NightInput[];
  hrv: MetricPoint[];
  heartRate: MetricPoint[];
  targetHours: number;
  /** epoch ms — injetado, nunca Date.now() interno. */
  now: number;
}

function insufficient(missing: MissingInput[], nightsNeeded: number): EstimatedScore {
  return { value: null, estimate: true, missing, nightsNeeded, parts: [] };
}

/** Noites ordenadas da mais recente para a mais antiga, só as já ocorridas. */
function orderedNights(input: ScoreInput): NightInput[] {
  return input.nights
    .filter((n) => {
      const w = nightWindow(n);
      return w !== null && w.end <= input.now;
    })
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/** Combina partes com pesos, redistribuindo o peso do que faltou. */
function combine(parts: Array<{ key: ScorePart["key"]; score: number | null; weight: number }>): {
  value: number;
  parts: ScorePart[];
} {
  const present = parts.filter((p): p is { key: ScorePart["key"]; score: number; weight: number } =>
    p.score !== null,
  );
  const totalWeight = present.reduce((s, p) => s + p.weight, 0);
  const norm = present.map((p) => ({ key: p.key, score: p.score, weight: p.weight / totalWeight }));
  const value = Math.round(norm.reduce((s, p) => s + p.score * p.weight, 0));
  return { value: clamp(value, ANCHORS.FLOOR, 100), parts: norm };
}

/**
 * RECUPERAÇÃO — HRV noturno + FC em repouso + qualidade de sono.
 *
 * Exige HRV da última noite E a linha de base de HRV. A FC em repouso é
 * OPCIONAL: quando falta, o peso dela é redistribuído entre as outras duas em
 * vez de a função devolver nada. Sono ausente também redistribui.
 */
export function recoveryScore(input: ScoreInput): EstimatedScore {
  const nights = orderedNights(input);
  if (nights.length === 0) return insufficient(["sleep"], ANCHORS.MIN_BASELINE_NIGHTS);

  const last = nights[0];
  const baselineNights = nights.slice(1, 1 + ANCHORS.BASELINE_NIGHTS);

  const hrvToday = nightlyHrv(input.hrv, last);
  if (hrvToday === null) return insufficient(["hrv"], 0);

  const hrvBase = median(
    baselineNights.map((n) => nightlyHrv(input.hrv, n)).filter((v): v is number => v !== null),
  );
  const usableBaseline = baselineNights.filter((n) => nightlyHrv(input.hrv, n) !== null).length;
  if (hrvBase === null || usableBaseline < ANCHORS.MIN_BASELINE_NIGHTS) {
    return insufficient(["baseline"], ANCHORS.MIN_BASELINE_NIGHTS - usableBaseline);
  }

  const rhrToday = restingHeartRate(input.heartRate, last);
  const rhrBase = median(
    baselineNights
      .map((n) => restingHeartRate(input.heartRate, n))
      .filter((v): v is number => v !== null),
  );

  const w = ANCHORS.RECOVERY_WEIGHTS;
  const { value, parts } = combine([
    { key: "hrv", score: scoreFromRatio(hrvToday / hrvBase, ANCHORS.HRV_BAND, true), weight: w.hrv },
    {
      key: "resting_hr",
      score:
        rhrToday !== null && rhrBase !== null && rhrBase > 0
          ? scoreFromRatio(rhrToday / rhrBase, ANCHORS.RHR_BAND, false)
          : null,
      weight: w.resting_hr,
    },
    { key: "sleep", score: sleepSubScore(last, input.targetHours), weight: w.sleep },
  ]);

  return { value, estimate: true, missing: [], nightsNeeded: 0, parts };
}

/**
 * PRONTIDÃO — qualidade de sono + TENDÊNCIA de HRV.
 *
 * A diferença para Recuperação não é cosmética: Recuperação olha a NOITE
 * (quanto você se recuperou), Prontidão olha PARA ONDE você vem indo (as
 * últimas TREND_NIGHTS contra a sua base). É por isso que a tendência entra
 * aqui e o valor absoluto entra lá.
 */
export function readinessScore(input: ScoreInput): EstimatedScore {
  const nights = orderedNights(input);
  if (nights.length === 0) return insufficient(["sleep"], ANCHORS.MIN_BASELINE_NIGHTS);

  const last = nights[0];
  const sleep = sleepSubScore(last, input.targetHours);
  if (sleep === null) return insufficient(["sleep"], 0);

  const recent = nights
    .slice(0, ANCHORS.TREND_NIGHTS)
    .map((n) => nightlyHrv(input.hrv, n))
    .filter((v): v is number => v !== null);

  const baselineNights = nights.slice(ANCHORS.TREND_NIGHTS, ANCHORS.TREND_NIGHTS + ANCHORS.BASELINE_NIGHTS);
  const baseVals = baselineNights
    .map((n) => nightlyHrv(input.hrv, n))
    .filter((v): v is number => v !== null);

  if (recent.length === 0) return insufficient(["hrv"], 0);
  if (baseVals.length < ANCHORS.MIN_BASELINE_NIGHTS) {
    return insufficient(["baseline"], ANCHORS.MIN_BASELINE_NIGHTS - baseVals.length);
  }

  const recentMed = median(recent);
  const baseMed = median(baseVals);
  if (recentMed === null || baseMed === null || baseMed <= 0) return insufficient(["baseline"], 1);

  const w = ANCHORS.READINESS_WEIGHTS;
  const { value, parts } = combine([
    { key: "sleep", score: sleep, weight: w.sleep },
    {
      key: "hrv_trend",
      score: scoreFromRatio(recentMed / baseMed, ANCHORS.HRV_TREND_BAND, true),
      weight: w.hrv_trend,
    },
  ]);

  return { value, estimate: true, missing: [], nightsNeeded: 0, parts };
}

/**
 * Converte as linhas cruas de `health_metrics` no formato desta biblioteca.
 * Fica aqui (e não no hook) para que o teste não precise de Supabase.
 */
export function toMetricPoints(
  rows: Array<{ metric_type: string; value: number | null; recorded_at: string }>,
  type: string,
): MetricPoint[] {
  const out: MetricPoint[] = [];
  for (const r of rows) {
    if (r.metric_type !== type || r.value === null) continue;
    const t = Date.parse(r.recorded_at);
    if (!Number.isFinite(t)) continue;
    out.push({ t, v: r.value });
  }
  return out;
}
